// Bottom sheet / full-screen surface (ADR-0047, UI-14).
//
// Built on native <dialog>, already this app's modal mechanism — no new
// dependency, and it brings modality, Esc-to-close, focus trapping and
// inertness for free.
//
// Two variants:
//   'bottom' — the quick-add drawer: rises from the bottom edge on a phone,
//              becomes a centred card on wider screens.
//   'full'   — the full task builder. ADR-0047 §1: the app has no router,
//              so the "builder view" is a full-height dialog rather than a
//              route. Android's hardware Back is treated by Chrome as a
//              close request for a modal dialog, which is what makes this
//              behave like a screen rather than a trap — VERIFY on a real
//              device (see the UI-16 acceptance note).

import { el } from '../dom.js';
import { icons } from '../icons.js';

// Android hardware-Back support (ADR-0047 §1, UI-16; ADR-0051). A sheet is a
// modal <dialog>, not a route — but the system Back gesture is a history
// navigation. Each open() pushes a history entry; a Back (popstate) closes the
// TOP sheet, so stacked sheets (quick-add → builder) peel one at a time. A
// user-initiated close pops the entry it pushed, suppressing the popstate that
// pop triggers.
//
// The handler is registered at MODULE LOAD — before the Vaadin Router is
// constructed (in showMain) — and calls stopImmediatePropagation() on any
// sheet-related pop, so the router never sees it. A sheet open/close is not a
// route change; letting the router handle it would re-render the current view
// (destroying just-added content and in-flight messages) and drop focus.
// Non-sheet pops fall through untouched, so route Back still navigates.
interface SheetStackEntry {
  closeFromPop: () => void;
}
const sheetStack: SheetStackEntry[] = [];
let suppressPop = false;

window.addEventListener('popstate', (ev) => {
  if (suppressPop) {
    suppressPop = false;
    ev.stopImmediatePropagation();
    return;
  }
  const top = sheetStack[sheetStack.length - 1];
  if (top) {
    ev.stopImmediatePropagation();
    top.closeFromPop();
  }
});

export interface SheetOptions {
  /** Heading shown at the top of the surface. */
  title: string;
  /** Localized accessible name for the dismiss control (icon-only). */
  closeLabel: string;
  variant?: 'bottom' | 'full';
  /** Stable id, so E2E specs and the harness can address the surface. */
  id?: string;
  /** Fired after the surface closes, however it was dismissed. */
  onClose?: () => void;
}

export interface Sheet {
  /** The <dialog>. Append it to the document once; open/close as needed. */
  root: HTMLDialogElement;
  /** Where callers put the form. */
  body: HTMLElement;
  open(): void;
  close(): void;
}

export function sheet(opts: SheetOptions): Sheet {
  const variant = opts.variant ?? 'bottom';
  const body = el('div', { class: 'sheet-body' });

  const dialog = el('dialog', {
    class: `sheet sheet-${variant}`,
    ...(opts.id ? { id: opts.id } : {}),
  }) as HTMLDialogElement;

  const closeBtn = el(
    'button',
    { class: 'icon-btn sheet-close', type: 'button', 'aria-label': opts.closeLabel, onclick: () => dialog.close() },
    icons.close(),
  );

  dialog.append(
    el('div', { class: 'sheet-head' }, el('h2', { class: 'sheet-title' }, opts.title), closeBtn),
    body,
  );

  // Clicking the backdrop dismisses. The <dialog> element's own box is what
  // receives the click when the backdrop is hit, so compare the target.
  dialog.addEventListener('click', (ev) => {
    if (ev.target === dialog) dialog.close();
  });

  // Whether THIS sheet currently owns a pushed history entry (so a
  // user-initiated close knows to pop it).
  let pushed = false;
  // The element focus should return to on close. Native <dialog> restores it
  // itself, but the router's focus management on the close-time popstate
  // clobbers that (focus lands on <body>), so the sheet re-asserts it after
  // the router settles.
  let opener: HTMLElement | null = null;
  const entry: SheetStackEntry = {
    closeFromPop: () => {
      // The Back already popped our entry; just close, don't pop again.
      pushed = false;
      if (dialog.open) dialog.close();
    },
  };

  // Native <dialog> restores focus to the opener on close by itself; what it
  // does NOT do is move focus INTO the surface on open — without this, a
  // keyboard or screen-reader user lands on the dialog container and has to
  // hunt for the first field (ADR-0036).
  dialog.addEventListener('close', () => {
    const i = sheetStack.lastIndexOf(entry);
    if (i >= 0) sheetStack.splice(i, 1);
    // Closed by the user (Esc / backdrop / a button): remove the history
    // entry we pushed. The popstate that history.back() fires is ours to
    // ignore, so suppress it — otherwise it would close the sheet beneath.
    if (pushed) {
      pushed = false;
      suppressPop = true;
      history.back();
    }
    // Re-assert focus on the opener after the router's popstate focus handling
    // runs (which otherwise drops focus to <body>). Deferred a frame so it
    // wins the race (ADR-0036: dismissing returns focus to the trigger).
    if (opener?.isConnected) {
      const target = opener;
      requestAnimationFrame(() => target.focus());
    }
    opts.onClose?.();
  });

  return {
    root: dialog,
    body,
    open(): void {
      opener = document.activeElement as HTMLElement | null;
      dialog.showModal();
      history.pushState({ ftSheet: opts.id ?? true }, '');
      pushed = true;
      sheetStack.push(entry);
      // Deferred a frame: showModal() runs the browser's own focusing step
      // (which lands on the first focusable element — the close button),
      // and that would undo a synchronous focus() call here.
      requestAnimationFrame(() => {
        // Material fields keep their <input> in a shadow root, so match the
        // host element too — `querySelector('input')` cannot see through it.
        dialog
          .querySelector<HTMLElement>('md-outlined-text-field, md-outlined-select, input, select, textarea')
          ?.focus();
      });
    },
    close(): void {
      dialog.close();
    },
  };
}
